import React, { useState, useEffect } from 'react';
import { X, ArrowRight, Check, Globe, MapPin, Activity, TrendingUp, Sparkles, ChevronRight } from 'lucide-react';
import { completeOnboarding, updateUserLocale, createBloodResult } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { UNITS, isSILocale, convertHaemoglobinForStorage, convertHaemoglobinForDisplay } from '../utils/unitFormatter';

// Step indicator component (moved outside to prevent re-renders)
const StepIndicator = ({ current, total }) => (
  <div className="flex justify-center gap-2 mb-6">
    {Array.from({ length: total }, (_, i) => (
      <div
        key={i}
        className={`h-1.5 rounded-full transition-all ${
          i < current ? 'w-8 bg-teal-500' : i === current ? 'w-8 bg-blue-500' : 'w-4 bg-slate-200'
        }`}
      />
    ))}
  </div>
);

// Location options with unit information - Shared country configuration
const BASE_LOCATIONS = [
  { code: 'AU', name: 'Australia', flag: '🇦🇺', units: 'SI (g/L, nmol/L)' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿', units: 'SI (g/L, nmol/L)' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', units: 'SI (g/L, nmol/L)' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', units: 'SI (g/L, nmol/L)' },
  { code: 'US', name: 'United States', flag: '🇺🇸', units: 'US (g/dL, ng/mL)' },
  { code: 'OTHER', name: 'Other / Not sure', flag: '🌍', units: 'SI (default)' },
];

// Helper to reorder locations with selected country first
const getOrderedLocations = (selectedCode) => {
  if (!selectedCode) return BASE_LOCATIONS;
  const selected = BASE_LOCATIONS.find(loc => loc.code === selectedCode);
  if (!selected) return BASE_LOCATIONS;
  return [selected, ...BASE_LOCATIONS.filter(loc => loc.code !== selectedCode)];
};

// Core CBC markers for guided entry (in order) - Full Complete Blood Count
const CORE_MARKERS = ['wbc', 'lymphocytes', 'neutrophils', 'haemoglobin', 'platelets'];

/**
 * Onboarding Flow Component
 * 
 * Redesigned for engagement:
 * - Guides users through 3 core blood markers one at a time
 * - Shows immediate visual feedback
 * - Creates an "aha" moment
 * - Under 2 minutes, calm tone
 */
const OnboardingFlow = ({ isReplay = false, onComplete, onSkip }) => {
  const { user, updateUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(isReplay ? 'replay-intro' : 'welcome');
  const [selectedLocation, setSelectedLocation] = useState(user?.country || 'AU');
  const [locationSaving, setLocationSaving] = useState(false);
  
  // Multi-step blood entry state - Full CBC (5 markers)
  const [currentMarkerIndex, setCurrentMarkerIndex] = useState(0); // 0-4 for the 5 markers
  const [markerValues, setMarkerValues] = useState({ 
    wbc: '', 
    lymphocytes: '', 
    neutrophils: '',
    haemoglobin: '', 
    platelets: '' 
  });
  const [resultDate, setResultDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);
  const [savedResults, setSavedResults] = useState([]); // Track what we've saved

  // Get haemoglobin config based on locale
  const getHaemoglobinConfig = () => {
    const isUS = selectedLocation === 'US';
    return {
      unit: isUS ? 'g/dL' : 'g/L',
      placeholder: isUS ? 'e.g., 14.5' : 'e.g., 145',
      normalRange: isUS ? '12.0 - 18.0' : '120 - 180'
    };
  };

  // Get marker config with dynamic haemoglobin - Full CBC markers
  const getMarkerConfig = (key) => {
    if (key === 'wbc') {
      return { key: 'wbc', label: 'White Blood Cells (WBC)', unit: UNITS.BLOOD_COUNT, placeholder: 'e.g., 7.5', normalRange: '4.0 - 11.0' };
    }
    if (key === 'lymphocytes') {
      return { key: 'lymphocytes', label: 'Lymphocytes', unit: UNITS.BLOOD_COUNT, placeholder: 'e.g., 2.5', normalRange: '1.0 - 4.0' };
    }
    if (key === 'neutrophils') {
      return { key: 'neutrophils', label: 'Neutrophils', unit: UNITS.BLOOD_COUNT, placeholder: 'e.g., 4.5', normalRange: '2.0 - 7.5' };
    }
    if (key === 'haemoglobin') {
      const hb = getHaemoglobinConfig();
      return { key: 'haemoglobin', label: 'Haemoglobin', ...hb };
    }
    if (key === 'platelets') {
      return { key: 'platelets', label: 'Platelets', unit: UNITS.BLOOD_COUNT, placeholder: 'e.g., 250', normalRange: '150 - 400' };
    }
    return null;
  };

  // Current marker being entered
  const currentMarkerKey = CORE_MARKERS[currentMarkerIndex];
  const currentMarker = getMarkerConfig(currentMarkerKey);
  const isLastMarker = currentMarkerIndex === CORE_MARKERS.length - 1;
  const currentValue = markerValues[currentMarkerKey] || '';

  // Try to infer location from browser on mount
  useEffect(() => {
    if (!isReplay && !user?.country) {
      const browserLocale = navigator.language || navigator.userLanguage;
      if (browserLocale) {
        const countryHint = browserLocale.split('-')[1]?.toUpperCase();
        if (countryHint && BASE_LOCATIONS.find(l => l.code === countryHint)) {
          setSelectedLocation(countryHint);
        }
      }
    }
  }, [isReplay, user?.country]);

  const handleSaveLocation = async () => {
    setLocationSaving(true);
    try {
      const locale = selectedLocation === 'OTHER' ? 'AU' : selectedLocation;
      await updateUserLocale(locale);
      updateUser({ country: locale });
      setCurrentStep('first-action');
    } catch (error) {
      console.error('Error saving location:', error);
      setCurrentStep('first-action');
    } finally {
      setLocationSaving(false);
    }
  };

  // Handle value change for current marker
  const handleMarkerValueChange = (value) => {
    setMarkerValues(prev => ({
      ...prev,
      [currentMarkerKey]: value
    }));
  };

  // Handle Next/Save for multi-step blood entry
  const handleNextMarker = async () => {
    const value = markerValues[currentMarkerKey];
    
    if (!value || isNaN(parseFloat(value))) {
      toast.error('Please enter a valid number');
      return;
    }

    setSaving(true);
    try {
      // Convert haemoglobin to storage format (g/L) if needed
      let storageValue = parseFloat(value);
      if (currentMarkerKey === 'haemoglobin') {
        storageValue = convertHaemoglobinForStorage(storageValue, selectedLocation);
      }
      
      // Save this marker's result
      const resultData = {
        date: resultDate,
        [currentMarkerKey]: storageValue
      };
      
      await createBloodResult(resultData);
      
      // Track saved result (store display value for preview)
      setSavedResults(prev => [...prev, {
        marker: currentMarkerKey,
        value: parseFloat(value), // Keep display value for preview
        storageValue: storageValue, // Also track storage value
        date: resultDate
      }]);

      if (isLastMarker) {
        // All markers done - go to preview
        setCurrentStep('preview');
      } else {
        // Advance to next marker silently (no success toast)
        setCurrentMarkerIndex(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error saving result:', error);
      // Don't block - continue anyway
      if (isLastMarker) {
        setCurrentStep('preview');
      } else {
        setCurrentMarkerIndex(prev => prev + 1);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSkipFirstAction = () => {
    setSavedResults([]);
    setCurrentStep('preview');
  };

  const handleComplete = async () => {
    try {
      if (!isReplay) {
        await completeOnboarding();
      }
      toast.success("You're ready to go!");
      onComplete();
    } catch (error) {
      console.error('Error completing onboarding:', error);
      onComplete();
    }
  };

  const handleSkip = () => {
    if (!isReplay) {
      completeOnboarding().catch(console.error);
    }
    onSkip();
  };

  // ============================================================================
  // REPLAY INTRO
  // ============================================================================
  if (currentStep === 'replay-intro') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 max-w-lg w-full relative">
          <button onClick={handleSkip} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
            <X className="h-6 w-6" />
          </button>

          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Quick Refresher</h2>
            <p className="text-slate-700 leading-relaxed">
              Let&apos;s walk through the key features again. You can exit anytime.
            </p>
          </div>

          <div className="flex gap-3">
            <button onClick={handleSkip} className="flex-1 py-3 px-6 border border-slate-300 text-slate-700 rounded-xl font-medium hover:bg-slate-50 transition-colors">
              Exit
            </button>
            <button onClick={() => setCurrentStep('welcome')} className="flex-1 py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2">
              Continue <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // STEP 1: WELCOME - More engaging, value-focused
  // ============================================================================
  if (currentStep === 'welcome') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 max-w-lg w-full relative">
          <button onClick={handleSkip} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
            <X className="h-6 w-6" />
          </button>

          <StepIndicator current={0} total={4} />

          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-400 to-blue-500 rounded-2xl mb-4 shadow-lg">
              <Activity className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">
              Welcome to Blood Cancer Companion
            </h2>
            <p className="text-slate-600 leading-relaxed">
              A private space to track your blood results, see trends over time, and stay organised for appointments.
            </p>
          </div>

          <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-4 mb-6 border border-teal-100">
            <p className="text-sm text-teal-800 text-center">
              <Sparkles className="h-4 w-4 inline mr-1" />
              In the next 2 minutes, you&apos;ll set up your account and see how tracking works.
            </p>
          </div>

          <button
            onClick={() => setCurrentStep('location')}
            className="w-full py-4 px-6 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-lg"
          >
            Let&apos;s get started
            <ArrowRight className="h-5 w-5" />
          </button>

          <button onClick={handleSkip} className="w-full mt-3 py-2 text-slate-500 text-sm hover:text-slate-700 transition-colors">
            Skip introduction
          </button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // STEP 2: LOCATION SELECTION
  // ============================================================================
  if (currentStep === 'location') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 max-w-lg w-full relative max-h-[90vh] overflow-y-auto">
          <button onClick={handleSkip} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
            <X className="h-6 w-6" />
          </button>

          <StepIndicator current={1} total={4} />

          <div className="text-center mb-5">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-3">
              <Globe className="h-6 w-6 text-blue-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Where are you located?</h2>
            <p className="text-sm text-slate-600">
              This ensures your blood test units match your healthcare system.
            </p>
          </div>

          <div className="space-y-2 mb-5">
            {getOrderedLocations(selectedLocation).map((location) => (
              <button
                key={location.code}
                onClick={() => setSelectedLocation(location.code)}
                className={`w-full p-3 rounded-xl border-2 transition-all flex items-center justify-between ${
                  selectedLocation === location.code
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{location.flag}</span>
                  <div className="text-left">
                    <p className="font-medium text-slate-900 text-sm">{location.name}</p>
                    <p className="text-xs text-slate-500">{location.units}</p>
                  </div>
                </div>
                {selectedLocation === location.code && <Check className="h-5 w-5 text-blue-600" />}
              </button>
            ))}
          </div>

          {selectedLocation === 'OTHER' && (
            <div className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-xs text-amber-800">
                <MapPin className="h-3 w-3 inline mr-1" />
                We&apos;ll use SI units. You can change this anytime in settings.
              </p>
            </div>
          )}

          <button
            onClick={handleSaveLocation}
            disabled={locationSaving}
            className="w-full py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {locationSaving ? 'Saving...' : 'Continue'}
            {!locationSaving && <ArrowRight className="h-5 w-5" />}
          </button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // STEP 3: FIRST ACTION - Guided multi-step blood entry (3 markers)
  // ============================================================================
  if (currentStep === 'first-action') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 max-w-lg w-full relative">
          <button onClick={handleSkip} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
            <X className="h-6 w-6" />
          </button>

          <StepIndicator current={2} total={4} />

          {/* Header with progress */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-teal-100 rounded-full mb-3">
              <TrendingUp className="h-6 w-6 text-teal-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              {currentMarkerIndex === 0 
                ? "Let's get your baseline" 
                : currentMarker.label}
            </h2>
            <p className="text-sm text-slate-600">
              {currentMarkerIndex === 0 
                ? "Enter your five core CBC markers — one step at a time."
                : "Keep going — you're doing great."}
            </p>
          </div>

          {/* Marker progress indicator - compact for 5 markers */}
          <div className="flex items-center justify-center gap-1 mb-6">
            {CORE_MARKERS.map((marker, idx) => {
              const config = getMarkerConfig(marker);
              const isActive = idx === currentMarkerIndex;
              const isCompleted = idx < currentMarkerIndex;
              
              // Short labels for compact display
              const shortLabels = {
                wbc: 'WBC',
                lymphocytes: 'Lymph',
                neutrophils: 'Neut',
                haemoglobin: 'Hb',
                platelets: 'Plt'
              };
              
              return (
                <div key={marker} className="flex items-center">
                  <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-all ${
                    isActive 
                      ? 'bg-teal-100 border-2 border-teal-500' 
                      : isCompleted 
                        ? 'bg-green-50 border border-green-300'
                        : 'bg-slate-100 border border-slate-200'
                  }`}>
                    {isCompleted ? (
                      <Check className="h-3 w-3 text-green-600" />
                    ) : (
                      <span className={`text-xs font-bold ${isActive ? 'text-teal-700' : 'text-slate-400'}`}>
                        {idx + 1}
                      </span>
                    )}
                    <span className={`text-xs font-medium ${
                      isActive ? 'text-teal-800' : isCompleted ? 'text-green-700' : 'text-slate-500'
                    }`}>
                      {shortLabels[marker]}
                    </span>
                  </div>
                  {idx < CORE_MARKERS.length - 1 && (
                    <div className={`w-2 h-0.5 ${isCompleted ? 'bg-green-300' : 'bg-slate-200'}`} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Current marker input */}
          <div className="bg-slate-50 rounded-xl p-5 mb-4">
            <label className="block text-sm font-semibold text-slate-800 mb-3">
              {currentMarker.label}
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.1"
                value={currentValue}
                onChange={(e) => handleMarkerValueChange(e.target.value)}
                placeholder={currentMarker.placeholder}
                className="w-full h-14 px-4 pr-24 rounded-xl border-2 border-slate-200 bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-100 text-lg transition-all"
                autoFocus
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium text-sm">
                {currentMarker.unit}
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Normal range: {currentMarker.normalRange} {currentMarker.unit}
            </p>
          </div>

          {/* Date input - only show on first marker, persists for all */}
          {currentMarkerIndex === 0 && (
            <div className="mb-5">
              <label className="block text-sm font-medium text-slate-700 mb-2">Test date</label>
              <input
                type="date"
                value={resultDate}
                onChange={(e) => setResultDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all"
              />
            </div>
          )}

          {/* Show selected date on subsequent markers */}
          {currentMarkerIndex > 0 && (
            <p className="text-xs text-slate-500 text-center mb-5">
              Test date: {new Date(resultDate).toLocaleDateString()}
            </p>
          )}

          {/* Action buttons */}
          <button
            onClick={handleNextMarker}
            disabled={saving || !currentValue}
            className="w-full py-3 px-6 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {saving ? 'Saving...' : isLastMarker ? 'Save and continue' : 'Next'}
            {!saving && <ArrowRight className="h-5 w-5" />}
          </button>

          {/* Progress text */}
          <p className="text-center text-xs text-slate-400 mt-3">
            {currentMarkerIndex + 1} of {CORE_MARKERS.length}
          </p>

          <button
            onClick={handleSkipFirstAction}
            className="w-full mt-2 py-3 text-slate-600 text-sm hover:text-slate-800 transition-colors flex items-center justify-center gap-1"
          >
            I don&apos;t have results yet
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // STEP 4: PREVIEW - Show visual result (supports multiple saved markers)
  // ============================================================================
  if (currentStep === 'preview') {
    const hasResults = savedResults.length > 0;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 max-w-lg w-full relative max-h-[90vh] overflow-y-auto">
          <button onClick={handleSkip} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
            <X className="h-6 w-6" />
          </button>

          <StepIndicator current={3} total={4} />

          {hasResults ? (
            // User entered results - show their CBC data with visual preview
            <>
              <div className="text-center mb-5">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-full mb-3">
                  <Check className="h-7 w-7 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 mb-2">
                  Your CBC baseline is saved!
                </h2>
                <p className="text-sm text-slate-600">
                  {savedResults.length === CORE_MARKERS.length 
                    ? "Complete blood count recorded. You're ready to track changes."
                    : `${savedResults.length} of ${CORE_MARKERS.length} markers recorded. Great start!`}
                </p>
              </div>

              {/* Visual CBC Chart Preview */}
              <div className="bg-slate-50 rounded-xl p-4 mb-4 border border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-slate-700">Your CBC Results</span>
                  <span className="text-xs text-slate-500">{new Date(resultDate).toLocaleDateString()}</span>
                </div>
                
                {/* Mini bar chart visualization */}
                <div className="grid grid-cols-5 gap-2 mb-3">
                  {savedResults.map((result, idx) => {
                    const config = getMarkerConfig(result.marker);
                    const shortLabels = { wbc: 'WBC', lymphocytes: 'Lymph', neutrophils: 'Neut', haemoglobin: 'Hb', platelets: 'Plt' };
                    return (
                      <div key={idx} className="flex flex-col items-center">
                        <div className="w-full h-12 bg-white rounded border border-slate-200 flex items-end justify-center p-1">
                          <div className="w-4 bg-teal-500 rounded-t" style={{ height: '70%' }} />
                        </div>
                        <span className="text-xs font-medium text-slate-600 mt-1">{shortLabels[result.marker]}</span>
                        <span className="text-xs text-teal-700 font-semibold">{result.value}</span>
                      </div>
                    );
                  })}
                </div>
                
                {/* Detailed list */}
                <div className="space-y-1.5 border-t border-slate-200 pt-3">
                  {savedResults.map((result, idx) => {
                    const config = getMarkerConfig(result.marker);
                    return (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">{config.label}</span>
                        <span className="font-medium text-slate-800">
                          {result.value} <span className="text-slate-500 text-xs">{config.unit}</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Info banner */}
              <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-4 mb-6 border border-teal-100">
                <div className="flex items-center gap-3">
                  <TrendingUp className="h-6 w-6 text-teal-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-teal-800">Track changes over time</p>
                    <p className="text-xs text-teal-700">
                      Add results after each blood test to see your trends on the dashboard.
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            // User skipped - show sample CBC preview
            <>
              <div className="text-center mb-5">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 rounded-full mb-3">
                  <TrendingUp className="h-7 w-7 text-blue-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 mb-2">
                  Here&apos;s what CBC tracking looks like
                </h2>
                <p className="text-sm text-slate-600">
                  Your complete blood count trends, all in one place.
                </p>
              </div>

              {/* Sample CBC chart preview */}
              <div className="bg-slate-50 rounded-xl p-4 mb-4 border border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-slate-700">Sample CBC Trends</span>
                  <span className="text-xs text-slate-500">Example data</span>
                </div>
                
                {/* Sample mini bar chart for all 5 markers */}
                <div className="grid grid-cols-5 gap-2 mb-3">
                  {[
                    { label: 'WBC', value: '7.2', height: '65%' },
                    { label: 'Lymph', value: '2.1', height: '55%' },
                    { label: 'Neut', value: '4.5', height: '70%' },
                    { label: 'Hb', value: '145', height: '80%' },
                    { label: 'Plt', value: '250', height: '60%' }
                  ].map((item, idx) => (
                    <div key={idx} className="flex flex-col items-center">
                      <div className="w-full h-12 bg-white rounded border border-slate-200 flex items-end justify-center p-1">
                        <div className="w-4 bg-blue-400 rounded-t opacity-60" style={{ height: item.height }} />
                      </div>
                      <span className="text-xs font-medium text-slate-500 mt-1">{item.label}</span>
                      <span className="text-xs text-slate-400">{item.value}</span>
                    </div>
                  ))}
                </div>
                
                {/* Trend preview */}
                <div className="relative h-16 bg-white rounded-lg border border-slate-200 p-2 mt-3">
                  <div className="absolute inset-2 flex items-end justify-between">
                    {[35, 45, 40, 50, 55, 48].map((height, i) => (
                      <div key={i} className="flex flex-col items-center">
                        <div 
                          className="w-2.5 h-2.5 bg-teal-500 rounded-full opacity-60"
                          style={{ marginBottom: `${height}%` }}
                        />
                      </div>
                    ))}
                  </div>
                  <p className="absolute bottom-1 left-0 right-0 text-center text-xs text-slate-400">
                    WBC trend over 6 months
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 rounded-xl p-4 mb-6 border border-blue-100">
                <div className="flex items-center gap-3">
                  <Activity className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <p className="text-sm text-blue-800">
                    Add your first CBC results from the dashboard whenever you&apos;re ready
                  </p>
                </div>
              </div>
            </>
          )}

          <button
            onClick={() => setCurrentStep('complete')}
            className="w-full py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
          >
            Continue
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // STEP 5: COMPLETE - Clear next steps
  // ============================================================================
  if (currentStep === 'complete') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 max-w-lg w-full relative">
          <StepIndicator current={4} total={4} />

          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-400 to-blue-500 rounded-2xl mb-4 shadow-lg">
              <Check className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              You&apos;re all set!
            </h2>
            <p className="text-slate-600">
              Your account is ready. Here&apos;s where to go next:
            </p>
          </div>

          {/* Clear next steps */}
          <div className="space-y-3 mb-6">
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
              <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-teal-700">1</span>
              </div>
              <div>
                <p className="font-medium text-slate-900 text-sm">Dashboard</p>
                <p className="text-xs text-slate-600">See your trends and add new results</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-blue-700">2</span>
              </div>
              <div>
                <p className="font-medium text-slate-900 text-sm">My Progress</p>
                <p className="text-xs text-slate-600">Enter and view all your blood test data</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
              <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-purple-700">3</span>
              </div>
              <div>
                <p className="font-medium text-slate-900 text-sm">Learn & Explore</p>
                <p className="text-xs text-slate-600">Articles about nutrition and supportive health</p>
              </div>
            </div>
          </div>

          <button
            onClick={handleComplete}
            className="w-full py-4 px-6 bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-600 hover:to-blue-600 text-white rounded-xl font-medium transition-all shadow-lg flex items-center justify-center gap-2 text-lg"
          >
            Go to Dashboard
            <ArrowRight className="h-5 w-5" />
          </button>

          <p className="text-center text-xs text-slate-500 mt-4">
            Replay this introduction anytime from your profile settings
          </p>
        </div>
      </div>
    );
  }

  return null;
};

export default OnboardingFlow;
